import React, { useState } from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { ChevronRight } from "@gaming/xds-react-js-icons";

import { getAssistantStageContent } from "../../constants/supportAssistantContent";
import { supportAssistantStore, useSupportAssistant } from "../../helpers/supportAssistantStore";
import { useStrings } from "../../localization/localizationHelpers";
import { AssistantInput } from "./AssistantInput";
import { ConfirmNewConversation } from "./ConfirmNewConversation";
import * as styles from "./SupportAssistant.module.css";

/** Flyout home: ask box, return-to-chat, and stage-based suggestions. */
export const AssistantHome: React.FC = () => {
  const { state, context } = useSupportAssistant();
  const [pendingQuestion, setPendingQuestion] = useState<string>();
  const content = getAssistantStageContent(state.home?.stage);
  const [returnToChat, suggestionsHeading] = useStrings(
    "supportAssistant_returnToChat",
    "supportAssistant_suggestionsHeading"
  );
  const suggestions = useStrings(...content.suggestionKeys);
  const hasConversation = state.messages.length > 0;

  // Asking on home replaces the current conversation, so confirm first (FR-029d).
  const confirmReplace = (question: string) => {
    if (hasConversation) {
      setPendingQuestion(question);
      return false;
    }
    return true;
  };

  return (
    <div data-testid="support-assistant-home">
      <section className={styles.section}>
        <AssistantInput onBeforeSend={confirmReplace} />
        {pendingQuestion !== undefined && (
          <ConfirmNewConversation
            onConfirm={() => {
              supportAssistantStore.clear();
              supportAssistantStore.send(pendingQuestion, context);
              setPendingQuestion(undefined);
            }}
            onCancel={() => setPendingQuestion(undefined)}
          />
        )}
      </section>
      {hasConversation && (
        <section className={styles.section}>
          <h3>{returnToChat}</h3>
          <button
            type="button"
            className={styles.card}
            onClick={() => supportAssistantStore.setView("chat")}
            data-testid="support-assistant-return"
          >
            <span className={styles.cardTitle}>{state.title}</span>
            <Icon aria-hidden="true" icon={ChevronRight} iconSize={IconSize.Custom} />
          </button>
        </section>
      )}
      <section className={styles.section}>
        <h3>{suggestionsHeading}</h3>
        <ul className={styles.chips}>
          {suggestions.map(suggestion => (
            <li key={suggestion}>
              <button
                type="button"
                className={styles.chip}
                onClick={() =>
                  confirmReplace(suggestion) && supportAssistantStore.send(suggestion, context)
                }
              >
                {suggestion}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
